import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  Collapse,
  useTheme,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCookies } from "react-cookie";
import { CCMPatient } from "../../interfaces/CCMPatient";
import PatientService from "../../services/PatientService";
import ChatService from "../../services/ChatService";
import { UserContext } from "../../context/UserContext";
import { format, subDays, differenceInYears } from "date-fns";
import { RPM_HISTORY_DAYS } from "../../components/CMS/MainScreen";
import ElapsedTimeWidget from "./ElapsedTimeWidget";
import { MeasurementCalendarDialog } from "./MeasurementCalendarDialog";
import { MeasurementTrendDialog } from "./MeasurementTrendDialog";
import TimeTracker from "./TimeTracker";
import { MeasurementGauge } from "./MeasurementGauge";
import { getTrafficLight } from "./CCMCentral";
import { MeasurementListDialog } from "./MeasurementListDialog";
import EngagementDetailsDialog from "./EngagementDetailsDialog";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonIcon from "@mui/icons-material/Person";
import WarningIcon from "@mui/icons-material/Warning";
import { PatientJitsiDialog } from "./PatientJitsiDialog";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

export const PatientDetails: React.FC = () => {
  const theme = useTheme();
  const { patientId } = useParams<{ patientId: string }>();
  const { t } = useTranslation();
  const [cookies] = useCookies(["access_token"]);
  const token = cookies.access_token;
  const userCtx = useContext(UserContext);
  const organization_id = userCtx?.user?.organization || "";

  // Function to get theme-aware colors for charts
  const getChartColors = () => {
    const isDarkMode = theme.palette.mode === "dark";
    return {
      bloodPressure: isDarkMode
        ? ["#87CEEB", "#98FB98"]
        : ["#000080", "#006400"], // Light blue/light green vs dark blue/dark green
      spo2: isDarkMode ? "#90EE90" : "#008000", // Light green vs dark green
      perfusionIndex: isDarkMode ? "#FFB6C1" : "#8B0000", // Light pink vs dark red
      fev1: isDarkMode ? "#E0FFFF" : "#0000FF", // Light cyan vs blue
      pef: isDarkMode ? "#DDA0DD" : "#800080", // Plum vs purple
      heartRate: isDarkMode ? "#FFA500" : "#FF4500", // Orange vs orange red
      weight: isDarkMode ? "#FF6347" : "#B22222", // Tomato vs firebrick
      glucose: isDarkMode ? "#DDA0DD" : "#800080", // Plum vs purple
    };
  };

  const chartColors = getChartColors();

  const [patient, setPatient] = useState<CCMPatient | null>(null);
  const [loading, setLoading] = useState(true);
  const [patientInteraction, setPatientInteraction] = useState<string | null>(
    null,
  );

  // Measurement data for graphs
  const [measurementData, setMeasurementData] = useState<any>(null);
  const [measurementLoading, setMeasurementLoading] = useState(false);
  // Baseline measurements (new API structure)
  const [baselineMeasurements, setBaselineMeasurements] = useState<any | null>(
    null,
  );

  // Dialog states
  const [openCalendarDialog, setOpenCalendarDialog] = useState(false);
  const [openTimeTracker, setOpenTimeTracker] = useState(false);
  const [selectedPatientForTracker, setSelectedPatientForTracker] = useState<
    string | null
  >(null);
  const [openEngagementDialog, setOpenEngagementDialog] = useState(false);
  const [selectedEngagementPatient, setSelectedEngagementPatient] = useState<
    string | null
  >(null);
  const [selectedEngagementDate, setSelectedEngagementDate] =
    useState<string>("");
  const [openListDialog, setOpenListDialog] = useState(false);
  const [openTrendDialog, setOpenTrendDialog] = useState(false);
  const [patientImageUrl, setPatientImageUrl] = useState<string | null>(null);
  const [openJitsiDialog, setOpenJitsiDialog] = useState(false);
  const [jitsiRoomName, setJitsiRoomName] = useState<string>("");

  // Panel collapse states - now for rows instead of individual panels
  const [vitalSignsCollapsed, setVitalSignsCollapsed] = useState(false);
  const [bottomRowCollapsed, setBottomRowCollapsed] = useState(false);

  // Charts per row control (1..4)
  const [chartsPerRow, setChartsPerRow] = useState<number>(4);

  // Duration selection for vital sign history
  type DurationOption = "30d" | "3m" | "6m" | "1y" | "fitAll";
  const [selectedDuration, setSelectedDuration] =
    useState<DurationOption>("30d");

  // Compute Grid md size and chart height based on chartsPerRow
  const chartColMd = Math.max(12 / chartsPerRow, 3); // ensure at least md=3
  const chartHeight =
    chartsPerRow === 1
      ? 420
      : chartsPerRow === 2
        ? 300
        : chartsPerRow === 3
          ? 260
          : 220;

  // Vital signs configuration
  const vitalSignsConfig = [
    {
      key: "blood_pressure",
      title: "Blood Pressure",
      unit: "mmHg",
      color: chartColors.bloodPressure[0],
      domain: undefined,
      dataKeys: ["systolic", "diastolic"],
      names: ["Systolic", "Diastolic"],
      colors: chartColors.bloodPressure,
      baselineKeys: ["baseline_systolic", "baseline_diastolic"],
    },
    {
      key: "oxygen_saturation",
      title: "SpO2",
      unit: "%",
      color: chartColors.spo2,
      domain: [90, 100],
      dataKeys: ["oxygen_saturation"],
      names: ["SpO2"],
      colors: [chartColors.spo2],
      baselineKeys: ["baseline_spo2"],
    },
    {
      key: "perfusion_index",
      title: "Perfusion Index",
      unit: "%",
      color: chartColors.perfusionIndex,
      domain: undefined,
      dataKeys: ["perfusion_index"],
      names: ["Perfusion Index"],
      colors: [chartColors.perfusionIndex],
      baselineKeys: [], // No baseline defined in interface
    },
    {
      key: "fev1",
      title: "FEV1",
      unit: "L",
      color: chartColors.fev1,
      domain: undefined,
      dataKeys: ["fev1"],
      names: ["FEV1"],
      colors: [chartColors.fev1],
      baselineKeys: ["baseline_fev1"],
    },
    {
      key: "pef",
      title: "PEF",
      unit: "L/min",
      color: chartColors.pef,
      domain: undefined,
      dataKeys: ["pef"],
      names: ["PEF"],
      colors: [chartColors.pef],
      baselineKeys: [], // No baseline defined in interface
    },
    {
      key: "heart_rate",
      title: "Heart Rate",
      unit: "bpm",
      color: chartColors.heartRate,
      domain: undefined,
      dataKeys: ["heart_rate"],
      names: ["Heart Rate"],
      colors: [chartColors.heartRate],
      baselineKeys: ["baseline_hr"],
    },
    {
      key: "weight",
      title: "Weight",
      unit: "lbs",
      color: chartColors.weight,
      domain: undefined,
      dataKeys: ["weight"],
      names: ["Weight"],
      colors: [chartColors.weight],
      baselineKeys: ["baseline_weight"],
    },
    {
      key: "glucose",
      title: "Glucose",
      unit: "mg/dL",
      color: chartColors.glucose,
      domain: undefined,
      dataKeys: ["glucose"],
      names: ["Glucose"],
      colors: [chartColors.glucose],
      baselineKeys: ["baseline_glucose"],
    },
  ];

  // Helper function to get baseline values
  const getBaselineValue = (baselineKey: string): number | null => {
    if (!patient || !baselineKey) return null;
    return (patient as any)[baselineKey] || null;
  };

  // Helper function to get latest measurement value and timestamp
  const getLatestMeasurement = (
    measurementType: string,
  ): { value: number | null; timestamp: Date | null } => {
    if (
      !measurementData?.history?.[measurementType] ||
      !Array.isArray(measurementData.history[measurementType])
    ) {
      return { value: null, timestamp: null };
    }

    const entries = measurementData.history[measurementType];
    if (entries.length === 0) return { value: null, timestamp: null };

    // Get the most recent entry (assuming entries are sorted by timestamp)
    const [timestamp, value] = entries[entries.length - 1];
    return { value, timestamp: new Date(timestamp) };
  };

  // Helper function to check if measurement is more than a week old
  const isMeasurementOld = (timestamp: Date | null): boolean => {
    if (!timestamp) return false;
    const oneWeekAgo = subDays(new Date(), 7);
    return timestamp < oneWeekAgo;
  };

  // Helper function to calculate deviation
  const calculateDeviation = (
    latest: number | null,
    baseline: number | null,
  ): string => {
    if (latest === null || baseline === null) return "-";
    const deviation = latest - baseline;
    const sign = deviation > 0 ? "+" : "";
    return `${sign}${deviation.toFixed(2)}`;
  };

  // Transform measurement data for charts
  const transformMeasurementData = (historyData: any) => {
    if (!historyData || typeof historyData !== "object") return [];

    const dataByTime = new Map<number, any>();

    // Process each measurement type
    Object.keys(historyData).forEach((type) => {
      const entries = historyData[type] || [];
      entries.forEach(([timestamp, value]: [string, number]) => {
        const time = new Date(timestamp).getTime() / 1000; // Convert to seconds for recharts
        if (!dataByTime.has(time)) {
          dataByTime.set(time, { time });
        }
        dataByTime.get(time)[type] = value;
      });
    });

    // Convert to array and sort by time
    return Array.from(dataByTime.values()).sort((a, b) => a.time - b.time);
  };

  const chartData = measurementData?.history
    ? transformMeasurementData(measurementData.history)
    : [];

  // Compute x-axis domain (values are in seconds since epoch in chartData)
  const computeXDomain = (): [number | "dataMin", number | "dataMax"] => {
    const now = Math.floor(Date.now() / 1000);
    if (selectedDuration === "fitAll") {
      if (chartData && chartData.length > 0) {
        const times = chartData.map((d: any) => d.time);
        return [Math.min(...times), Math.max(...times)];
      }
      return ["dataMin", "dataMax"];
    }
    let startMs: number;
    if (selectedDuration === "30d") {
      startMs = Math.floor(subDays(new Date(), 30).getTime() / 1000);
    } else if (selectedDuration === "3m") {
      startMs = Math.floor(subDays(new Date(), 90).getTime() / 1000);
    } else if (selectedDuration === "6m") {
      startMs = Math.floor(subDays(new Date(), 180).getTime() / 1000);
    } else {
      // 1y
      startMs = Math.floor(subDays(new Date(), 365).getTime() / 1000);
    }
    return [startMs, now];
  };
  const xDomain = computeXDomain();

  // Notes functionality
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState("");

  // Chat functionality
  const [chatMessages, setChatMessages] = useState<
    { role: "user" | "assistant"; content: string }[]
  >([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const chatMessagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, chatLoading]);

  // Fetch patient details once when patientId / token / organization change.
  useEffect(() => {
    if (!patientId || !token || !organization_id) return;

    const fetchPatientDetails = async () => {
      try {
        setLoading(true);
        const patientsUrl = PatientService.getPatients(
          organization_id,
          RPM_HISTORY_DAYS,
        );
        const response = await fetch(patientsUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const patients = await response.json();
        const foundPatient = patients.find(
          (p: CCMPatient) => p.patient_id === patientId,
        );
        if (foundPatient) {
          console.log("Found patient details:", foundPatient);
          setPatient(foundPatient);
        }
      } catch (error) {
        console.error("Error fetching patient details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPatientDetails();
  }, [patientId, token, organization_id]);

  // Decode patient image from patient details
  useEffect(() => {
    if (!patient) return;

    if (patient.image) {
      // Check if it's already a data URI or base64 string
      if (patient.image.startsWith("data:image")) {
        setPatientImageUrl(patient.image);
      } else {
        // Assume it's base64-encoded image data, add data URI prefix
        setPatientImageUrl(`data:image/jpeg;base64,${patient.image}`);
      }
    } else {
      setPatientImageUrl(null);
    }
  }, [patient]);

  // Fetch measurement data when patientId, token, organization_id or selectedDuration change.
  useEffect(() => {
    if (!patientId || !token || !organization_id) return;

    const fetchMeasurementData = async () => {
      try {
        setMeasurementLoading(true);
        // Compute start date based on selectedDuration
        const now = new Date();
        let startDate: string;
        if (selectedDuration === "fitAll") {
          startDate = "1900-01-01T00:00";
        } else if (selectedDuration === "30d") {
          startDate = format(subDays(now, 30), "yyyy-MM-dd'T'HH:mm");
        } else if (selectedDuration === "3m") {
          startDate = format(subDays(now, 90), "yyyy-MM-dd'T'HH:mm");
        } else if (selectedDuration === "6m") {
          startDate = format(subDays(now, 180), "yyyy-MM-dd'T'HH:mm");
        } else {
          // '1y'
          startDate = format(subDays(now, 365), "yyyy-MM-dd'T'HH:mm");
        }
        const endDate = format(new Date(), "yyyy-MM-dd'T'HH:mm");

        const response = await PatientService.getMeasurementHistory(
          token,
          patientId,
          organization_id,
          startDate,
          endDate,
        );
        setMeasurementData(response);
      } catch (error) {
        console.error("Error fetching measurement data:", error);
      } finally {
        setMeasurementLoading(false);
      }
    };

    fetchMeasurementData();
  }, [patientId, token, organization_id, selectedDuration]);

  // Fetch baselines for plotting reference lines
  useEffect(() => {
    const fetchBaselines = async () => {
      if (!patientId || !token) return;
      try {
        const res = await PatientService.getPatientBaselines(token, patientId);
        setBaselineMeasurements(res?.measurements || null);
      } catch (e) {
        setBaselineMeasurements(null);
      }
    };
    fetchBaselines();
  }, [patientId, token]);

  const handleToggleInteraction = (patientId: string) => {
    setSelectedPatientForTracker(patientId);
    setPatientInteraction(patientId);
    setOpenTimeTracker(true);
  };

  const handleStartJitsiCall = (currentPatientId: string) => {
    const roomName = currentPatientId.trim().replace(/\s+/g, "-");
    setJitsiRoomName(roomName);
    setOpenJitsiDialog(true);
  };

  const handleCloseJitsiDialog = () => {
    setOpenJitsiDialog(false);
  };

  const handleSendChatMessage = async () => {
    const message = chatInput.trim();
    if (!message || chatLoading) return;

    // Add user message
    setChatMessages((prev) => [...prev, { role: "user", content: message }]);
    setChatInput("");
    setChatLoading(true);

    try {
      // Call the chat API
      const response = await ChatService.sendChatRequest(
        token,
        message,
        patientId || "",
        organization_id,
        "RPM",
      );

      if (response && response.response) {
        // Add assistant response
        // The API might return the response object wrapped in a 'response' field
        // We need to extract the actual text message from the inner 'response' field if it exists
        let content = response.response;
        if (typeof content === "object" && content !== null) {
          // Check if the object has a 'response' field (which contains the text)
          if (
            (content as any).response &&
            typeof (content as any).response === "string"
          ) {
            content = (content as any).response;
          } else {
            // Fallback: stringify the object if we can't find the text
            content = JSON.stringify(content);
          }
        }
        setChatMessages((prev) => [
          ...prev,
          { role: "assistant", content: content },
        ]);
      } else {
        // Handle error case
        setChatMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              t("chat_error_message") ||
              "Sorry, I couldn't process your request. Please try again.",
          },
        ]);
      }
    } catch (error) {
      console.error("Chat error:", error);
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            t("chat_error_message") ||
            "Sorry, an error occurred. Please try again.",
        },
      ]);
    } finally {
      setChatLoading(false);
      // Focus the input field after response
      setTimeout(() => {
        chatInputRef.current?.focus();
      }, 0);
    }
  };

  const handleSaveNotes = () => {
    // Placeholder for saving notes
    console.log("Saving notes:", notes);
    setNotesOpen(false);
  };

  const handleOpenCalendarDialog = () => {
    setOpenCalendarDialog(true);
  };

  const handleCloseCalendarDialog = () => {
    setOpenCalendarDialog(false);
  };

  const handleTimeTrackerClose = async (
    patientId: string | null,
    utc_time: string | null,
  ) => {
    if (patientId) {
      // Get the updated call time from back-end
      const response = await PatientService.fetchTimeData(token, patientId);
      if (response) {
        const callTime =
          (response as any).rpm_call_time ??
          (response as any).call_time ??
          null;
        setPatient((prev) =>
          prev
            ? {
                ...prev,
                call_time: callTime ?? prev.call_time,
                last_contact: utc_time ?? prev.last_contact,
              }
            : prev,
        );
      }
    }
    setOpenTimeTracker(false);
    setSelectedPatientForTracker(null);
    setPatientInteraction(null);
  };

  const handleTrafficLightClick = (patient: CCMPatient, date: string) => {
    setSelectedEngagementPatient(patient.patient_id);
    setSelectedEngagementDate(date);
    setOpenEngagementDialog(true);
  };

  const handleCloseEngagementDialog = () => {
    setOpenEngagementDialog(false);
  };

  const handleOpenListDialog = () => {
    setOpenListDialog(true);
  };

  const handleOpenTrendDialog = () => {
    setOpenTrendDialog(true);
  };

  const handleCloseTrendDialog = () => {
    setOpenTrendDialog(false);
  };

  const handleCloseListDialog = () => {
    setOpenListDialog(false);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography>{t("loading_patient_details")}</Typography>
      </Box>
    );
  }

  if (!patient) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography>{t("patient_not_found")}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3} sx={{ minHeight: 400 }}>
        {/* Top Row - Patient Information Panels */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
                  {/* Patient Information Panel */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <CardContent sx={{ flex: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {t("Patient Information")}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Box
                          display="flex"
                          alignItems="center"
                          sx={{ width: "100%" }}
                        >
                          {/* Left: text (50%) */}
                          <Box sx={{ width: "50%", pr: 2 }}>
                            <Stack spacing={1}>
                              <Typography>
                                <strong>{t("Name")}:</strong>{" "}
                                {patient.last_name}, {patient.first_name}
                              </Typography>
                              <Typography>
                                <strong>{t("Patient ID")}:</strong>{" "}
                                {patient.patient_id}
                              </Typography>
                              <Typography>
                                <strong>{t("Gender")}:</strong>{" "}
                                {patient.gender || t("not_specified")}
                              </Typography>
                              <Typography>
                                <strong>{t("Age")}:</strong>{" "}
                                {patient.dob
                                  ? `${differenceInYears(new Date(), new Date(patient.dob))} ${t("years")}`
                                  : t("not_specified")}
                              </Typography>
                              <Typography>
                                <strong>{t("Groups")}:</strong>{" "}
                                {patient.patient_groups?.join(", ") ||
                                  t("None")}
                              </Typography>
                            </Stack>
                          </Box>

                          {/* Right: avatar (50%) */}
                          <Box
                            sx={{
                              width: "50%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            {/* Placeholder avatar; if patient.photo exists, use it */}
                            <Box sx={{ textAlign: "center" }}>
                              {/* Use a square placeholder with border */}
                              <Box
                                sx={{
                                  width: 120,
                                  height: 120,
                                  borderRadius: 1,
                                  overflow: "hidden",
                                  border: `1px solid ${theme.palette.divider}`,
                                  bgcolor:
                                    theme.palette.mode === "dark"
                                      ? theme.palette.background.default
                                      : "#fff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {patientImageUrl ? (
                                  <img
                                    src={patientImageUrl}
                                    alt="Patient"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <PersonIcon
                                    sx={{
                                      fontSize: 48,
                                      color: theme.palette.text.secondary,
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Box>

                        {/* Full-width divider and Contact/Session moved to Compliance & Engagement panel */}
                        <Divider sx={{ my: 2 }} />
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Information Panel - Current State & Condition Summary */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <CardContent sx={{ flex: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {t("Current State & Condition Summary")}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              {t("Vital Signs")}:
                            </Typography>
                            <TableContainer
                              component={Box}
                              sx={{ maxHeight: 300 }}
                            >
                              <Table size="small">
                                <TableHead
                                  sx={{
                                    "& .MuiTableCell-root": {
                                      fontSize: "0.9rem",
                                    },
                                  }}
                                >
                                  <TableRow>
                                    <TableCell sx={{ py: 0.5, px: 1 }}>
                                      {t("Parameter")}
                                    </TableCell>
                                    <TableCell
                                      sx={{ py: 0.5, px: 1 }}
                                      align="center"
                                    >
                                      {t("Last Recorded")}
                                    </TableCell>
                                    <TableCell
                                      sx={{ py: 0.5, px: 1 }}
                                      align="center"
                                    >
                                      {t("Baseline")}
                                    </TableCell>
                                    <TableCell
                                      sx={{ py: 0.5, px: 1 }}
                                      align="center"
                                    >
                                      {t("Deviation")}
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {/* Heart Rate - only show if baseline exists */}
                                  {(() => {
                                    const baseline =
                                      getBaselineValue("baseline_hr");
                                    if (!baseline) return null;
                                    const latest =
                                      getLatestMeasurement("heart_rate");
                                    const isOld = isMeasurementOld(
                                      latest.timestamp,
                                    );
                                    return (
                                      <TableRow key="heart_rate">
                                        <TableCell
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {t("Heart Rate")}
                                          {isOld && (
                                            <Tooltip
                                              title={t(
                                                "measurement_old_warning",
                                              )}
                                            >
                                              <WarningIcon
                                                sx={{
                                                  ml: 0.5,
                                                  fontSize: "1rem",
                                                  color: "warning.main",
                                                }}
                                              />
                                            </Tooltip>
                                          )}
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {latest.value
                                            ? `${latest.value} bpm`
                                            : "-"}
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {baseline} bpm
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {calculateDeviation(
                                            latest.value,
                                            baseline,
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })()}

                                  {/* SpO2 - only show if baseline exists */}
                                  {(() => {
                                    const baseline =
                                      getBaselineValue("baseline_spo2");
                                    if (!baseline) return null;
                                    const latest =
                                      getLatestMeasurement("oxygen_saturation");
                                    const isOld = isMeasurementOld(
                                      latest.timestamp,
                                    );
                                    return (
                                      <TableRow key="spo2">
                                        <TableCell
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {t("SpO2")}
                                          {isOld && (
                                            <Tooltip
                                              title={t(
                                                "measurement_old_warning",
                                              )}
                                            >
                                              <WarningIcon
                                                sx={{
                                                  ml: 0.5,
                                                  fontSize: "1rem",
                                                  color: "warning.main",
                                                }}
                                              />
                                            </Tooltip>
                                          )}
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {latest.value
                                            ? `${latest.value}%`
                                            : "-"}
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {baseline}%
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {calculateDeviation(
                                            latest.value,
                                            baseline,
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })()}

                                  {/* Blood Pressure - only show if baseline exists */}
                                  {(() => {
                                    const baselineSystolic =
                                      getBaselineValue("baseline_systolic");
                                    const baselineDiastolic =
                                      getBaselineValue("baseline_diastolic");
                                    if (!baselineSystolic || !baselineDiastolic)
                                      return null;
                                    const latestSystolic = getLatestMeasurement(
                                      "blood_pressure_systolic",
                                    );
                                    const latestDiastolic =
                                      getLatestMeasurement(
                                        "blood_pressure_diastolic",
                                      );
                                    const isOld =
                                      isMeasurementOld(
                                        latestSystolic.timestamp,
                                      ) ||
                                      isMeasurementOld(
                                        latestDiastolic.timestamp,
                                      );
                                    return (
                                      <TableRow key="blood_pressure">
                                        <TableCell
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {t("Blood Pressure")}
                                          {isOld && (
                                            <Tooltip
                                              title={t(
                                                "measurement_old_warning",
                                              )}
                                            >
                                              <WarningIcon
                                                sx={{
                                                  ml: 0.5,
                                                  fontSize: "1rem",
                                                  color: "warning.main",
                                                }}
                                              />
                                            </Tooltip>
                                          )}
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {latestSystolic.value &&
                                          latestDiastolic.value
                                            ? `${latestSystolic.value}/${latestDiastolic.value} mmHg`
                                            : "-"}
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {baselineSystolic}/{baselineDiastolic}{" "}
                                          mmHg
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {latestSystolic.value &&
                                          latestDiastolic.value
                                            ? `${calculateDeviation(latestSystolic.value, baselineSystolic)}/${calculateDeviation(latestDiastolic.value, baselineDiastolic)}`
                                            : "-"}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })()}

                                  {/* Weight - only show if baseline exists */}
                                  {(() => {
                                    const baseline =
                                      getBaselineValue("baseline_weight");
                                    if (!baseline) return null;
                                    const latest =
                                      getLatestMeasurement("weight");
                                    const isOld = isMeasurementOld(
                                      latest.timestamp,
                                    );
                                    return (
                                      <TableRow key="weight">
                                        <TableCell
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {t("Weight")}
                                          {isOld && (
                                            <Tooltip
                                              title={t(
                                                "measurement_old_warning",
                                              )}
                                            >
                                              <WarningIcon
                                                sx={{
                                                  ml: 0.5,
                                                  fontSize: "1rem",
                                                  color: "warning.main",
                                                }}
                                              />
                                            </Tooltip>
                                          )}
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {latest.value
                                            ? `${latest.value} lbs`
                                            : "-"}
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {baseline} lbs
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {calculateDeviation(
                                            latest.value,
                                            baseline,
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })()}

                                  {/* Glucose - only show if baseline exists */}
                                  {(() => {
                                    const baseline =
                                      getBaselineValue("baseline_glucose");
                                    if (!baseline) return null;
                                    const latest =
                                      getLatestMeasurement("glucose");
                                    const isOld = isMeasurementOld(
                                      latest.timestamp,
                                    );
                                    return (
                                      <TableRow key="glucose">
                                        <TableCell
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {t("Glucose")}
                                          {isOld && (
                                            <Tooltip
                                              title={t(
                                                "measurement_old_warning",
                                              )}
                                            >
                                              <WarningIcon
                                                sx={{
                                                  ml: 0.5,
                                                  fontSize: "1rem",
                                                  color: "warning.main",
                                                }}
                                              />
                                            </Tooltip>
                                          )}
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {latest.value
                                            ? `${latest.value} mg/dL`
                                            : "-"}
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {baseline} mg/dL
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {calculateDeviation(
                                            latest.value,
                                            baseline,
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })()}

                                  {/* FEV1 - only show if baseline exists */}
                                  {(() => {
                                    const baseline =
                                      getBaselineValue("baseline_fev1");
                                    if (!baseline) return null;
                                    const latest = getLatestMeasurement("fev1");
                                    const isOld = isMeasurementOld(
                                      latest.timestamp,
                                    );
                                    return (
                                      <TableRow key="fev1">
                                        <TableCell
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {t("FEV1")}
                                          {isOld && (
                                            <Tooltip
                                              title={t(
                                                "measurement_old_warning",
                                              )}
                                            >
                                              <WarningIcon
                                                sx={{
                                                  ml: 0.5,
                                                  fontSize: "1rem",
                                                  color: "warning.main",
                                                }}
                                              />
                                            </Tooltip>
                                          )}
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {latest.value
                                            ? `${latest.value} L`
                                            : "-"}
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {baseline} L
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            fontSize: "0.875rem",
                                            py: 0.5,
                                            px: 1,
                                          }}
                                        >
                                          {calculateDeviation(
                                            latest.value,
                                            baseline,
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })()}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>

                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Combined Compliance & Engagement Panel */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <CardContent
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography variant="h6" gutterBottom>
                          {t("Compliance & Engagement")}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          {/* Measurement Compliance Section */}
                          <Box>
                            {/* Titles Row */}
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={1}
                            >
                              <Typography variant="subtitle1">
                                {t("Measurement Compliance")}
                              </Typography>
                              <Typography variant="subtitle1">
                                {t("Measurements")}
                              </Typography>
                            </Box>

                            {/* Content Row */}
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              {patient.measurement_days != null ? (
                                <Box
                                  onClick={handleOpenCalendarDialog}
                                  sx={{ cursor: "pointer", flex: 1 }}
                                >
                                  <MeasurementGauge
                                    measurementDays={patient.measurement_days}
                                  />
                                </Box>
                              ) : (
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  No measurement data available
                                </Typography>
                              )}
                              <Box sx={{ textAlign: "center" }}>
                                <Tooltip title={t("View All Measurements")}>
                                  <FormatListNumberedIcon
                                    onClick={handleOpenListDialog}
                                    style={{
                                      cursor: "pointer",
                                      fontSize: 34,
                                      color: "secondary",
                                    }}
                                  />
                                </Tooltip>
                              </Box>
                            </Box>
                          </Box>

                          {/* Recent Engagement Section */}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              {t("Recent Engagement (Last")} {RPM_HISTORY_DAYS}{" "}
                              {t("Days)")}
                            </Typography>

                            <Box
                              sx={{
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {patient.patient_groups &&
                              patient.engagementhistory ? (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  justifyContent="space-between"
                                  sx={{ width: "100%" }}
                                >
                                  {Array.from({ length: RPM_HISTORY_DAYS }).map(
                                    (_, index) => {
                                      const reversedIndex =
                                        RPM_HISTORY_DAYS - 1 - index;
                                      const engagement =
                                        patient.engagementhistory?.[
                                          reversedIndex
                                        ];
                                      const engagementStatus =
                                        engagement?.engagement || "NO PLAN";
                                      const isSelectable =
                                        engagementStatus !== "NO PLAN";
                                      const dayLabel =
                                        reversedIndex === 0
                                          ? t("Today")
                                          : reversedIndex === 1
                                            ? t("Yesterday")
                                            : format(
                                                subDays(
                                                  new Date(),
                                                  reversedIndex,
                                                ),
                                                "EEE",
                                              );

                                      return (
                                        <Box
                                          key={index}
                                          display="flex"
                                          flexDirection="column"
                                          alignItems="center"
                                          sx={{ flex: 1 }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontSize: "0.7rem",
                                              lineHeight: 1.2,
                                            }}
                                          >
                                            {dayLabel}
                                          </Typography>
                                          <Tooltip
                                            title={`${dayLabel}: ${engagementStatus}`}
                                          >
                                            <Box
                                              onClick={
                                                isSelectable
                                                  ? () =>
                                                      handleTrafficLightClick(
                                                        patient,
                                                        engagement?.date,
                                                      )
                                                  : undefined
                                              }
                                              sx={{
                                                cursor: isSelectable
                                                  ? "pointer"
                                                  : "default",
                                                mt: 0.5,
                                              }}
                                            >
                                              {getTrafficLight(
                                                engagementStatus,
                                              )}
                                            </Box>
                                          </Tooltip>
                                        </Box>
                                      );
                                    },
                                  )}
                                </Stack>
                              ) : (
                                <Typography variant="body2">
                                  {t("no_engagement_data")}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Full-width divider and Contact/Session placed below the two-column area */}
                          <Divider sx={{ my: -1 }} />
                          <Box sx={{ mt: 1 }}>
                            <Stack spacing={1}>
                              <Typography variant="body2">
                                <strong>{t("Last Contact")}:</strong>{" "}
                                {patient.last_contact
                                  ? format(
                                      new Date(patient.last_contact),
                                      "MMM dd, yyyy HH:mm",
                                    )
                                  : t("Never")}
                              </Typography>

                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Box>
                                  <Typography variant="subtitle2" gutterBottom>
                                    {t("Call Time")}:
                                  </Typography>
                                  <ElapsedTimeWidget
                                    elapsedMinutes={patient.call_time}
                                    patient_id={patient.patient_id}
                                    organization_id={organization_id}
                                    refreshKey={patient.last_contact}
                                  />
                                </Box>

                                <Stack spacing={1} alignItems="stretch">
                                  <Button
                                    variant="contained"
                                    color={
                                      patientInteraction === patient.patient_id
                                        ? "secondary"
                                        : "primary"
                                    }
                                    onClick={() =>
                                      handleToggleInteraction(
                                        patient.patient_id,
                                      )
                                    }
                                    disabled={
                                      patientInteraction !== null &&
                                      patientInteraction !== patient.patient_id
                                    }
                                    sx={{ minWidth: 140 }}
                                  >
                                    {patientInteraction === patient.patient_id
                                      ? t("In Progress")
                                      : t("Start Session")}
                                  </Button>

                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() =>
                                      handleStartJitsiCall(patient.patient_id)
                                    }
                                    sx={{ minWidth: 140 }}
                                  >
                                    {t("Start Call")}
                                  </Button>
                                </Stack>
                              </Box>
                            </Stack>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
          </Grid>
        </Grid>{" "}
        {/* Vital Signs History Row */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ pb: 1 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="h6">{t("Vital Signs History")}</Typography>

                <Box display="flex" alignItems="center" gap={2}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="vitals-duration-label">
                      {t("Time Range")}
                    </InputLabel>
                    <Select
                      labelId="vitals-duration-label"
                      value={selectedDuration}
                      label={t("Time Range")}
                      onChange={(e) =>
                        setSelectedDuration(e.target.value as any)
                      }
                    >
                      <MenuItem value={"30d"}>{t("Last 30 Days")}</MenuItem>
                      <MenuItem value={"3m"}>{t("Last 3 Months")}</MenuItem>
                      <MenuItem value={"6m"}>{t("Last 6 Months")}</MenuItem>
                      <MenuItem value={"1y"}>{t("Last Year")}</MenuItem>
                      <MenuItem value={"fitAll"}>{t("Fit All")}</MenuItem>
                    </Select>
                  </FormControl>

                  <Tooltip title={t("View Trends")}>
                    <QueryStatsIcon
                      onClick={handleOpenTrendDialog}
                      style={{
                        cursor: "pointer",
                        fontSize: 28,
                        color: theme.palette.primary.main,
                      }}
                    />
                  </Tooltip>

                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="charts-per-row-label">
                      {t("Charts per row")}
                    </InputLabel>
                    <Select
                      labelId="charts-per-row-label"
                      value={chartsPerRow}
                      label={t("Charts per row")}
                      onChange={(e) => setChartsPerRow(Number(e.target.value))}
                    >
                      <MenuItem value={1}>1</MenuItem>
                      <MenuItem value={2}>2</MenuItem>
                      <MenuItem value={3}>3</MenuItem>
                      <MenuItem value={4}>4</MenuItem>
                    </Select>
                  </FormControl>

                  <IconButton
                    size="small"
                    onClick={() => setVitalSignsCollapsed(!vitalSignsCollapsed)}
                  >
                    {vitalSignsCollapsed ? (
                      <ExpandMoreIcon />
                    ) : (
                      <ExpandLessIcon />
                    )}
                  </IconButton>
                </Box>
              </Box>
              <Collapse in={!vitalSignsCollapsed}>
                <Divider sx={{ mb: 2 }} />

                {measurementLoading ? (
                  <Typography>{t("loading_measurement_data")}</Typography>
                ) : chartData.length > 0 ? (
                  <Grid container spacing={2}>
                    {vitalSignsConfig
                      .filter((config) =>
                        config.dataKeys.some((key) =>
                          chartData.some(
                            (item: any) => item[key] !== undefined,
                          ),
                        ),
                      )
                      .map((config) => {
                        // derive times for this panel from chartData
                        const times: number[] = chartData.reduce(
                          (acc: number[], item: any) => {
                            const hasValue = config.dataKeys.some(
                              (k) => item[k] !== undefined && item[k] !== null,
                            );
                            if (hasValue) acc.push(item.time);
                            return acc;
                          },
                          [],
                        );

                        // compute domain based on available times (dynamic sizing)
                        let panelXDomain: [
                          number | "dataMin",
                          number | "dataMax",
                        ] = ["dataMin", "dataMax"];
                        if (times.length === 1) {
                          const t = times[0];
                          // show a small window around the single point
                          panelXDomain = [t - 3600, t + 3600];
                        } else if (times.length > 1) {
                          const minT = Math.min(...times);
                          const maxT = Math.max(...times);
                          const pad = Math.max(
                            Math.floor((maxT - minT) * 0.04),
                            60,
                          ); // 4% padding or min 60s
                          panelXDomain = [minT - pad, maxT + pad];
                        } else {
                          // no times -> fall back to selected range
                          panelXDomain = xDomain;
                        }

                        // estimate pixel spacing to decide dot sizing
                        const estimatedWidth =
                          typeof window !== "undefined"
                            ? Math.floor(
                                window.innerWidth * (chartColMd / 12) * 0.9,
                              )
                            : 600;
                        const pointCount = Math.max(1, times.length);
                        const spacingPx = estimatedWidth / pointCount;
                        const showDots = spacingPx >= 8 && pointCount <= 300; // hide dots if too dense
                        const dotRadius = showDots
                          ? Math.max(1.5, Math.min(4, spacingPx / 4))
                          : 0;

                        return (
                          <Grid item xs={12} md={chartColMd} key={config.key}>
                            <Typography variant="subtitle1" gutterBottom>
                              {config.title}
                            </Typography>
                            <Box sx={{ height: chartHeight }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData}>
                                  <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke={
                                      theme.palette.mode === "dark"
                                        ? "rgba(255,255,255,0.06)"
                                        : "rgba(0,0,0,0.08)"
                                    }
                                  />
                                  <XAxis
                                    dataKey="time"
                                    type="number"
                                    domain={panelXDomain}
                                    tickFormatter={(time) =>
                                      format(new Date(time * 1000), "MM/dd")
                                    }
                                  />
                                  <YAxis domain={config.domain} />
                                  <RechartsTooltip />
                                  {/* Render baseline reference lines */}
                                  {config.baselineKeys.map((baselineKey) => {
                                    const baselineValue =
                                      getBaselineValue(baselineKey);
                                    if (baselineValue === null) return null;
                                    return (
                                      <ReferenceLine
                                        key={`${config.key}-${baselineKey}-baseline`}
                                        y={baselineValue}
                                        stroke={
                                          theme.palette.mode === "dark"
                                            ? "#32CD32"
                                            : "#006400"
                                        }
                                        strokeDasharray="5 5"
                                        strokeWidth={2}
                                      />
                                    );
                                  })}
                                  {/* Baselines from new API (component-based) */}
                                  {(() => {
                                    const elems: JSX.Element[] = [];
                                    const stroke =
                                      theme.palette.mode === "dark"
                                        ? "#32CD32"
                                        : "#006400";
                                    const dash = "5 5";
                                    const add = (
                                      val?: number,
                                      key?: string,
                                    ) => {
                                      if (val === undefined || val === null)
                                        return;
                                      elems.push(
                                        <ReferenceLine
                                          key={`${config.key}-${key}-baseline-new`}
                                          y={val}
                                          stroke={stroke}
                                          strokeDasharray={dash}
                                          strokeWidth={2}
                                        />,
                                      );
                                    };
                                    switch (config.key) {
                                      case "oxygen_saturation":
                                        add(
                                          baselineMeasurements?.SpO2
                                            ?.oxygen_saturation?.value,
                                          "SpO2-oxygen",
                                        );
                                        break;
                                      case "perfusion_index":
                                        add(
                                          baselineMeasurements?.SpO2
                                            ?.perfusion_index?.value,
                                          "SpO2-perfusion",
                                        );
                                        break;
                                      case "blood_pressure":
                                        add(
                                          baselineMeasurements?.BloodPressure
                                            ?.systolic?.value,
                                          "BP-sys",
                                        );
                                        add(
                                          baselineMeasurements?.BloodPressure
                                            ?.diastolic?.value,
                                          "BP-dia",
                                        );
                                        break;
                                      case "fev1":
                                        add(
                                          baselineMeasurements?.PeakFlow?.fev1
                                            ?.value,
                                          "PF-fev1",
                                        );
                                        break;
                                      case "pef":
                                        add(
                                          baselineMeasurements?.PeakFlow?.pef
                                            ?.value,
                                          "PF-pef",
                                        );
                                        break;
                                      case "heart_rate":
                                        add(
                                          baselineMeasurements?.HeartRate
                                            ?.heart_rate?.value,
                                          "HR",
                                        );
                                        break;
                                      case "weight":
                                        add(
                                          baselineMeasurements?.Weight?.weight
                                            ?.value,
                                          "Weight",
                                        );
                                        break;
                                      case "glucose":
                                        add(
                                          baselineMeasurements?.BloodGlucose
                                            ?.glucose?.value,
                                          "Glucose",
                                        );
                                        break;
                                      default:
                                        break;
                                    }
                                    return elems;
                                  })()}
                                  {config.dataKeys.map((key, index) => {
                                    const baseColor =
                                      config.colors[index] ||
                                      config.color ||
                                      (theme.palette.mode === "dark"
                                        ? "#90EE90"
                                        : "#006400");
                                    const strokeColor =
                                      theme.palette.mode === "dark"
                                        ? baseColor
                                        : baseColor;

                                    return (
                                      <Line
                                        key={key}
                                        type="monotone"
                                        dataKey={key}
                                        stroke={strokeColor}
                                        strokeWidth={2.2}
                                        strokeLinecap="round"
                                        name={`${config.names[index]} (${config.unit})`}
                                        connectNulls={true}
                                        isAnimationActive={false}
                                        dot={
                                          showDots
                                            ? {
                                                r: dotRadius * 0.5,
                                                stroke: strokeColor,
                                                fill: strokeColor,
                                              }
                                            : false
                                        }
                                        activeDot={
                                          showDots
                                            ? {
                                                r: Math.min(
                                                  6,
                                                  (dotRadius + 2) * 0.5,
                                                ),
                                                stroke: strokeColor,
                                                fill: strokeColor,
                                              }
                                            : false
                                        }
                                      />
                                    );
                                  })}
                                </LineChart>
                              </ResponsiveContainer>
                            </Box>
                          </Grid>
                        );
                      })}

                    {vitalSignsConfig.every(
                      (config) =>
                        !config.dataKeys.some((key) =>
                          chartData.some(
                            (item: any) => item[key] !== undefined,
                          ),
                        ),
                    ) && (
                      <Grid item xs={12}>
                        <Typography color="textSecondary">
                          {t("no_vital_signs_range")}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                ) : (
                  <Typography color="textSecondary">
                    {t("unable_load_measurement")}
                  </Typography>
                )}
              </Collapse>
            </CardContent>
          </Card>
        </Grid>
        {/* Bottom Row - Chat and Notes */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ pb: 1 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="h6">
                  {t("Communication & Notes")}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setBottomRowCollapsed(!bottomRowCollapsed)}
                >
                  {bottomRowCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                </IconButton>
              </Box>
              <Collapse in={!bottomRowCollapsed}>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  {/* Notes Panel */}
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                        >
                          <Typography variant="h6">
                            {t("Patient Notes")}
                          </Typography>
                          <Button
                            variant="outlined"
                            onClick={() => setNotesOpen(true)}
                          >
                            {t("Edit Notes")}
                          </Button>
                        </Box>
                        <Divider sx={{ mb: 2 }} />

                        <Box
                          sx={{
                            minHeight: 100,
                            p: 2,
                            borderRadius: 1,
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? theme.palette.background.paper
                                : "#f9f9f9",
                            color:
                              theme.palette.mode === "dark"
                                ? theme.palette.text.primary
                                : undefined,
                          }}
                        >
                          {notes ? (
                            <Typography sx={{ whiteSpace: "pre-wrap" }}>
                              {notes}
                            </Typography>
                          ) : (
                            <Typography
                              color={
                                theme.palette.mode === "dark"
                                  ? "text.secondary"
                                  : "textSecondary"
                              }
                            >
                              {t("no_notes_available")}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Collapse>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Notes Dialog */}
      <Dialog
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("Edit Patient Notes")}</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={10}
            fullWidth
            placeholder={t("enter_patient_notes")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>{t("Cancel")}</Button>
          <Button onClick={handleSaveNotes} variant="contained">
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      <PatientJitsiDialog
        open={openJitsiDialog}
        onClose={handleCloseJitsiDialog}
        roomName={jitsiRoomName}
        token={token}
        displayName={userCtx?.user?.username}
        email={userCtx?.user?.email}
        closeLabel={t("close")}
      />

      {/* Measurement Calendar Dialog */}
      <MeasurementCalendarDialog
        patientId={patient?.patient_id || ""}
        organization_id={organization_id}
        token={token}
        open={openCalendarDialog}
        onClose={handleCloseCalendarDialog}
        engagementHistory={patient?.engagementhistory}
      />

      {/* Measurement Trend Dialog (reuse the central dialog) */}
      {patient && (
        <MeasurementTrendDialog
          patient={patient}
          conditionType={
            patient.patient_groups
              ? patient.patient_groups.join(", ")
              : patient.patient_groups
          }
          organization_id={organization_id}
          token={token}
          open={openTrendDialog}
          onClose={handleCloseTrendDialog}
        />
      )}

      {/* Time Tracker Dialog */}
      {selectedPatientForTracker && (
        <TimeTracker
          patientId={selectedPatientForTracker}
          open={openTimeTracker}
          onClose={handleTimeTrackerClose}
        />
      )}

      {/* Engagement Details Dialog */}
      {openEngagementDialog && selectedEngagementPatient && (
        <EngagementDetailsDialog
          open={openEngagementDialog}
          onClose={handleCloseEngagementDialog}
          patient_id={selectedEngagementPatient}
          date={selectedEngagementDate}
          token={token}
        />
      )}

      {/* Measurement List Dialog */}
      {patient && (
        <MeasurementListDialog
          patientId={patient.patient_id}
          conditionType={
            patient.patient_groups
              ? patient.patient_groups.join(", ")
              : patient.patient_groups
          }
          organization_id={organization_id}
          token={token}
          open={openListDialog}
          onClose={handleCloseListDialog}
        />
      )}
    </Box>
  );
};
